import React, { useState, useEffect, useMemo, SyntheticEvent } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  CircularProgress,
  createFilterOptions,
  FilterOptionsState
} from '@mui/material';
import { CollegeSearchResult, ActivePercentiles } from './ScoreProgressComparison'; // Assuming interfaces are exported

// Define the structure of the raw data from the JSON file
interface RawCollegeData {
  College_Name: string;
  IPEDS_ID: number;
  '2023_SAT_Scores'?: {
    Math_75th_percentile?: number | null;
    Math_50th_percentile?: number | null;
    Math_25th_percentile?: number | null;
    Reading_and_Writing_75th_percentile?: number | null;
    Reading_and_Writing_50th_percentile?: number | null;
    Reading_and_Writing_25th_percentile?: number | null;
  };
  // We might not need 2022 scores for this feature, but good to have in the interface if fetched
}

export interface CollegeScoreSearchProps {
  onCollegeSelect: (college: CollegeSearchResult) => void;
  onPercentileChange: (percentiles: ActivePercentiles) => void;
  initialPercentiles: ActivePercentiles;
  maxColleges: number;
  selectedCount: number;
}

const transformData = (rawData: RawCollegeData[]): CollegeSearchResult[] => {
  return rawData
    .filter(college => college['2023_SAT_Scores']) // Only include colleges with 2023 scores
    .map(college => {
      const scores2023 = college['2023_SAT_Scores']!;
      return {
        name: college.College_Name,
        mathScores: {
          '25th': scores2023.Math_25th_percentile ?? null,
          '50th': scores2023.Math_50th_percentile ?? null,
          '75th': scores2023.Math_75th_percentile ?? null,
        },
        readingWritingScores: {
          '25th': scores2023.Reading_and_Writing_25th_percentile ?? null,
          '50th': scores2023.Reading_and_Writing_50th_percentile ?? null,
          '75th': scores2023.Reading_and_Writing_75th_percentile ?? null,
        },
      };
    });
};

// Explicitly type filterOptions for Autocomplete to handle potential null values if the Autocomplete itself can be null.
// However, our options list (allColleges) does not contain nulls.
// The issue arises if Autocomplete<CollegeSearchResult | null> is used, implying the *value* can be null, not necessarily the options list.
// For `filterOptions`, it operates on the `options` prop. So, if `options` are `CollegeSearchResult[]`, then this is correct.
const filterOptions = createFilterOptions<CollegeSearchResult>({
  matchFrom: 'any',
  limit: 100, 
  stringify: (option) => option.name 
});

export const CollegeScoreSearch: React.FC<CollegeScoreSearchProps> = ({ 
  onCollegeSelect, 
  onPercentileChange, 
  initialPercentiles, 
  maxColleges, 
  selectedCount 
}) => {
  const [allColleges, setAllColleges] = useState<CollegeSearchResult[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [activePercentiles, setActivePercentiles] = useState<ActivePercentiles>(initialPercentiles);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCollegeData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/university_sat_scores.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const rawData: RawCollegeData[] = await response.json();
        const transformed = transformData(rawData);
        setAllColleges(transformed);
        setError(null);
      } catch (e) {
        console.error("Failed to fetch or parse college data:", e);
        setError("Failed to load college data. Please try again later.");
        setAllColleges([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCollegeData();
  }, []);

  useEffect(() => {
    onPercentileChange(activePercentiles);
  }, [activePercentiles, onPercentileChange]);

  const handleCollegeChange = (
    event: SyntheticEvent,
    newValue: CollegeSearchResult | null 
  ) => {
    if (newValue) {
      onCollegeSelect(newValue);
      setSearchValue(''); // Clear search after selection
    }
  };

  const handlePercentileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPercentiles = {
      ...activePercentiles,
      [event.target.name]: event.target.checked,
    };
    setActivePercentiles(newPercentiles);
  };
  
  const memoizedColleges = useMemo(() => allColleges, [allColleges]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
      <CircularProgress />
      <Typography sx={{ ml: 2 }}>Loading college data...</Typography>
    </Box>;
  }

  if (error) {
    return <Typography color="error" sx={{ my: 2 }}>{error}</Typography>;
  }

  const isMaxReached = selectedCount >= maxColleges;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center', flex: 1 }}>
      <Autocomplete<CollegeSearchResult, false, false, false> 
        options={memoizedColleges}
        getOptionLabel={(option: CollegeSearchResult) => option.name}
        value={null} // Always null since we clear after selection
        inputValue={searchValue}
        onInputChange={(event, newInputValue) => setSearchValue(newInputValue)}
        onChange={handleCollegeChange}
        isOptionEqualToValue={(option: CollegeSearchResult, value: CollegeSearchResult) => option.name === value.name}
        filterOptions={filterOptions}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label={isMaxReached ? `Max colleges reached (${maxColleges})` : "Search College"} 
            variant="outlined"
            disabled={isMaxReached}
            helperText={`${selectedCount}/${maxColleges} colleges selected`}
          />
        )}
        noOptionsText={allColleges.length === 0 && !loading ? "No colleges found or data error." : "No options"}
        sx={{ width: { xs: '100%', sm: 300 } }}
        disabled={isMaxReached}
      />
      <FormGroup row sx={{ justifyContent: 'center', flexShrink: 0 }}>
        <FormControlLabel
          control={<Checkbox checked={activePercentiles['25th']} onChange={handlePercentileChange} name="25th" />}
          label="25th percentile"
        />
        <FormControlLabel
          control={<Checkbox checked={activePercentiles['50th']} onChange={handlePercentileChange} name="50th" />}
          label="50th percentile"
        />
        <FormControlLabel
          control={<Checkbox checked={activePercentiles['75th']} onChange={handlePercentileChange} name="75th" />}
          label="75th percentile"
        />
      </FormGroup>
    </Box>
  );
};

export default CollegeScoreSearch; 